import { faClone } from "@jewlr/pro-light-svg-icons/faClone"
import PropTypes from "prop-types"
import { useState } from "react"
import { CopyToClipboard } from "react-copy-to-clipboard"
import { useDispatch, useSelector } from "react-redux"

import { applyCoupon } from "areas/cart/store/actions"
import withTotalItemPrice from "areas/product/hoc/with-total-item-price"
import Button from "components/ui/button"
import FontAwesomeIcon from "components/ui/font-awesome-icon"
import { to_currency } from "helpers/number"
import { cn } from "styles/utils"

const ProgressSegment = ({
  cartTotal,
  inPdp,
  prevthresholdMin,
  threshold,
  totalItemPrice,
}) => {
  let lightGreenPct, solidGreenPct

  if (inPdp) {
    lightGreenPct =
      ((Math.min(threshold.min, cartTotal + totalItemPrice) -
        prevthresholdMin) /
        (threshold.min - prevthresholdMin)) *
      100
    lightGreenPct = Math.trunc(Math.max(0, lightGreenPct))
  }
  solidGreenPct =
    ((Math.min(threshold.min, cartTotal) - prevthresholdMin) /
      (threshold.min - prevthresholdMin)) *
    100
  solidGreenPct = Math.trunc(Math.max(0, solidGreenPct))

  return (
    <div className="flex-1 text-center">
      <div
        aria-hidden
        className={cn(
          "relative h-6 w-full rounded-full",
          solidGreenPct !== 100 && "border border-black"
        )}
      >
        {/* light green bar */}
        {lightGreenPct > 0 && lightGreenPct > solidGreenPct && (
          <div
            aria-hidden
            className="bg-jade absolute h-full rounded-full opacity-80"
            style={{ width: `${lightGreenPct}%` }}
          />
        )}
        {/* solid green bar */}
        {solidGreenPct > 0 && (
          <div
            aria-hidden
            className="bg-emerald absolute h-full rounded-full"
            style={{ width: `${solidGreenPct}%` }}
          />
        )}
      </div>
      <div
        className={cn(
          "text-11 desktop:text-13 mt-12 flex flex-col justify-center",
          !inPdp && "desktop:flex-row gap-4"
        )}
      >
        <p>
          Spend {to_currency(threshold.min)}
          {threshold.max ? `-${threshold.max}` : "+"}
        </p>
        <p className="font-semibold">
          Get {to_currency(threshold.deduction)} off
        </p>
      </div>
    </div>
  )
}

ProgressSegment.propTypes = {
  cartTotal: PropTypes.number,
  inPdp: PropTypes.bool,
  prevthresholdMin: PropTypes.number,
  threshold: PropTypes.object,
  totalItemPrice: PropTypes.number,
}

const ThresholdProgressBar = ({ inPdp, totalItemPrice = 0 }) => {
  const [copied, setCopied] = useState(false)
  const [applyingCoupon, setApplyingCoupon] = useState(false)
  const [couponError, setCouponError] = useState("")
  const { title, thresholds, coupon_code } = sl.config.jewlr_days_promo
  const cartTotal = useSelector(
    (state) =>
      state.cart.summary.total_before_addons_and_discount ??
      state.cart.summary.cart_total
  )
  const lid = useSelector((state) => state.wizardUI.lid)
  const lineItems = useSelector((state) => state.cart.line_items)
  const couponApplied = useSelector(
    (state) => state.cart.summary?.promo_code?.promo_code_applied
  )
  const dispatch = useDispatch()

  let adjustedCartTotal = cartTotal
  // When update line_item in pdp, we want to exclude current line_item from cartTotal
  if (lid && lineItems && lineItems.length > 0 && inPdp) {
    const lineItem = lineItems.find((li) => li.id === lid)
    const lineItemTotal =
      (lineItem?.price || 0) +
      (lineItem?.addons || []).reduce(
        (accu, curr) => (accu += curr.price || 0),
        0
      )
    adjustedCartTotal -= lineItemTotal || 0
  }

  const currTierIndex = thresholds.findIndex(
    (threshold) =>
      cartTotal < threshold.min ||
      (cartTotal >= threshold.min && cartTotal <= (threshold.max || Infinity))
  )
  const currTier = thresholds[currTierIndex] ?? thresholds[0]
  const nextTier =
    currTierIndex + 1 < thresholds.length ? thresholds[currTierIndex + 1] : null

  return (
    <div
      className={cn(
        "p-20",
        inPdp
          ? "bg-grey-4 desktop:p-30"
          : "bg-grey-10 desktop:bg-grey-4 desktop:grid grid-cols-[1fr_auto] items-center"
      )}
    >
      <h2
        className={cn(
          "body-lg text-center font-semibold",
          !inPdp && "desktop:text-left col-start-1 row-start-1"
        )}
      >
        {title}
      </h2>
      <div aria-live="polite" className="sr-only">
        {currTierIndex === 0
          ? `You are ${to_currency(currTier.min - cartTotal)} away from getting ${to_currency(currTier.deduction)} off.`
          : nextTier
            ? `You've unlocked ${to_currency(currTier.deduction)} off! Use CODE ${coupon_code} in Cart to activate the discount. Spend ${to_currency(nextTier.min - cartTotal)} more to get ${to_currency(nextTier.deduction)} off.`
            : `Congratulations! You've unlocked the maximum discount of ${to_currency(currTier.deduction)} off. Use CODE ${coupon_code} in Cart to activate the discount.`}
      </div>
      <div
        className={cn(
          "desktop:gap-12 mt-16 flex gap-8",
          !inPdp && "desktop:gap-28 col-span-full row-start-2"
        )}
      >
        {thresholds.map((threshold, i) => (
          <ProgressSegment
            cartTotal={adjustedCartTotal}
            inPdp={inPdp}
            key={`threshold-progress-segment-${i}`}
            prevthresholdMin={i === 0 ? 0 : thresholds[i - 1].min}
            threshold={threshold}
            totalItemPrice={totalItemPrice}
          />
        ))}
      </div>
      <div
        className={cn(
          "mt-16 w-full text-center",
          !inPdp && "desktop:mt-0 col-start-2 row-start-1 justify-self-end"
        )}
      >
        {inPdp ? (
          <CopyToClipboard onCopy={() => setCopied(true)} text={coupon_code}>
            <Button
              className="max-w-(--desktop-width) border-black-print mx-auto w-full border uppercase"
              style={{ "--desktop-width": "224px" }}
              variant="tertiary"
            >
              {copied ? (
                "COPIED!"
              ) : (
                <>
                  Code: {coupon_code}{" "}
                  <FontAwesomeIcon className="ml-10" icon={faClone} />
                </>
              )}
            </Button>
          </CopyToClipboard>
        ) : couponApplied?.toUpperCase() === coupon_code.toUpperCase() ? (
          <p className="text-emerald text-16 font-semibold uppercase">
            Discount Applied!
          </p>
        ) : (
          <Button
            className="w-(--desktop-width) border-black-print mx-auto border uppercase"
            loading={applyingCoupon}
            onClick={() => {
              setCouponError("")
              setApplyingCoupon(true)
              dispatch(
                applyCoupon(
                  coupon_code,
                  null,
                  setApplyingCoupon,
                  setCouponError
                )
              )
            }}
            style={{ "--desktop-width": "224px" }}
            variant="tertiary"
          >
            {!applyingCoupon && <span>Code: {coupon_code}</span>}
          </Button>
        )}
        {couponError && (
          <p className="text-error text-12 mt-6">{couponError}</p>
        )}
      </div>
    </div>
  )
}

ThresholdProgressBar.propTypes = {
  inPdp: PropTypes.bool,
  totalItemPrice: PropTypes.number,
}

export default ThresholdProgressBar

export const PdpThresholdProgressBar = withTotalItemPrice(ThresholdProgressBar)
